<template>
  <v-main>
    <v-container fluid>
      <!-- Header with Balance -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl" class="primary">
            <v-card-text class="text-center white--text py-6">
              <h2 class="text-h4 font-weight-bold mb-2">Lucky Wheel</h2>
              <div class="text-h5">
                <v-icon large color="amber">mdi-coin</v-icon>
                Your Balance: {{ userBalance }} Coins
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="isLoading">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Available Wheels -->
      <v-row v-else>
        <v-col
          v-for="(wheel, index) in wheels"
          :key="`wheel_${index}`"
          md="6"
          lg="4"
        >
          <v-card
            elevation="6"
            rounded="xxl"
            class="wheel-card"
            :class="{ 'selected-wheel': selectedWheel && selectedWheel.id === wheel.id }"
          >
            <v-card-title class="gradient-header white--text">
              <v-icon class="mr-2" color="white" size="28">mdi-ferris-wheel</v-icon>
              <h3>{{ wheel.name }}</h3>
            </v-card-title>

            <v-card-text class="pt-4">
              <div class="text-center mb-4">
                <p class="text--secondary">{{ wheel.description }}</p>
              </div>

              <v-divider class="my-3"></v-divider>

              <!-- Spin Costs -->
              <v-row dense class="mb-3">
                <v-col cols="6">
                  <v-card outlined class="text-center pa-3">
                    <v-icon color="amber">mdi-coin</v-icon>
                    <div class="text-h6 font-weight-bold mt-1">
                      {{ wheel.spin_cost }}
                    </div>
                    <div class="text-caption text--secondary">1 Spin</div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="text-center pa-3">
                    <v-icon color="amber">mdi-gold</v-icon>
                    <div class="text-h6 font-weight-bold mt-1">
                      {{ wheel.ten_spin_cost }}
                    </div>
                    <div class="text-caption text--secondary">10 Spins</div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Rewards Preview -->
              <v-divider class="my-3"></v-divider>
              <div class="rewards-preview">
                <div class="text-caption text--secondary mb-2 text-center">
                  <v-icon small>mdi-gift</v-icon>
                  {{ wheel.active_items ? wheel.active_items.length : 0 }} Possible Rewards
                </div>
                <div class="d-flex flex-wrap justify-center">
                  <v-chip
                    v-for="(item, i) in (wheel.active_items || []).slice(0, 4)"
                    :key="`item_${i}`"
                    x-small
                    :color="item.color || 'primary'"
                    dark
                    class="ma-1"
                  >
                    {{ item.reward_name }}
                  </v-chip>
                  <v-chip
                    v-if="wheel.active_items && wheel.active_items.length > 4"
                    x-small
                    outlined
                    class="ma-1"
                  >
                    +{{ wheel.active_items.length - 4 }} more
                  </v-chip>
                </div>
              </div>
            </v-card-text>

            <v-card-actions class="justify-center pb-4">
              <v-btn
                color="primary"
                :disabled="userBalance < wheel.spin_cost"
                @click="openSpinDialog(wheel, 1)"
              >
                <v-icon left>mdi-rotate-right</v-icon>
                Spin Once
              </v-btn>
              <v-btn
                color="secondary"
                :disabled="userBalance < wheel.ten_spin_cost"
                @click="openSpinDialog(wheel, 10)"
              >
                <v-icon left>mdi-rotate-3d-variant</v-icon>
                Spin 10x
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Spin History Button -->
      <v-row>
        <v-col md="12" class="text-center">
          <v-btn
            large
            color="info"
            outlined
            @click="$router.push('/lucky-wheels/history')"
          >
            <v-icon left>mdi-history</v-icon>
            View Spin History
          </v-btn>
        </v-col>
      </v-row>

      <!-- Spin Confirmation Dialog -->
      <v-dialog v-model="spinDialog" max-width="500px" persistent>
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon color="white" left>mdi-ferris-wheel</v-icon>
            Confirm Spin
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="primary">mdi-rotate-right</v-icon>
              <h3 class="mt-4">{{ selectedWheel ? selectedWheel.name : "" }}</h3>
              <p class="text-h5 mt-2">
                <v-icon color="amber">mdi-coin</v-icon>
                Cost: {{ spinCost }} Coins
              </p>
              <p class="text-h6">
                Spins: {{ spinCount }}x
              </p>
              <p class="mt-4 text--secondary">
                Your balance: {{ userBalance }} coins<br />
                After spin: {{ userBalance - spinCost }} coins
              </p>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="spinDialog = false" :disabled="spinning">Cancel</v-btn>
            <v-btn color="primary" :loading="spinning" @click="spin">
              <v-icon left>mdi-play</v-icon>
              Spin Now!
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Results Dialog -->
      <v-dialog v-model="resultsDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="success white--text">
            <v-icon color="white" left>mdi-trophy</v-icon>
            Spin Results!
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="text-center mb-4">
              <v-icon size="80" color="success">mdi-gift</v-icon>
              <h3 class="text-h4 mt-2">Congratulations!</h3>
            </div>

            <!-- Results List -->
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Reward</th>
                    <th class="text-left">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(result, i) in spinResults" :key="`result_${i}`">
                    <td>
                      <div class="d-flex align-center">
                        <div
                          v-if="result.color"
                          :style="{
                            backgroundColor: result.color,
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            marginRight: '8px',
                          }"
                        ></div>
                        <strong>{{ result.reward_name }}</strong>
                      </div>
                    </td>
                    <td>
                      <span v-if="result.reward_type === 'coins'">
                        <v-icon small color="amber">mdi-coin</v-icon>
                        {{ result.reward_value }}
                      </span>
                      <span v-else class="text--secondary">{{ result.reward_type }}</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Summary -->
            <v-divider class="my-4"></v-divider>
            <div class="text-center">
              <div class="text-h6">
                <v-icon color="amber">mdi-coin</v-icon>
                Total Won: {{ totalWinnings }} Coins
              </div>
              <div class="text-h5 mt-2 primary--text font-weight-bold">
                New Balance: {{ newBalance }} Coins
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="closeResultsDialog">
              <v-icon left>mdi-check</v-icon>
              Awesome!
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "LuckyWheelSpinPage",
  data() {
    return {
      isLoading: false,
      wheels: [],
      userBalance: 0,
      selectedWheel: null,
      spinCount: 1,
      spinCost: 0,
      spinDialog: false,
      resultsDialog: false,
      spinning: false,
      spinResults: [],
      totalWinnings: 0,
      newBalance: 0,
    };
  },
  mounted() {
    this.getAvailableWheels();
  },
  methods: {
    async getAvailableWheels() {
      this.isLoading = true;
      try {
        const response = await axios.get("/lucky-wheel/available");

        if (response.data.success) {
          this.wheels = response.data.data;
          this.userBalance = response.data.user_balance;
        }
      } catch (error) {
        console.error("Error fetching wheels:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openSpinDialog(wheel, count) {
      this.selectedWheel = wheel;
      this.spinCount = count;
      this.spinCost = count === 1 ? wheel.spin_cost : wheel.ten_spin_cost;
      this.spinDialog = true;
    },
    async spin() {
      this.spinning = true;
      try {
        const response = await axios.post("/lucky-wheel/spin", {
          lucky_wheel_id: this.selectedWheel.id,
          spin_count: this.spinCount,
        });

        if (response.data.success) {
          const data = response.data.data;
          this.spinResults = data.spins;
          this.totalWinnings = data.total_winnings;
          this.newBalance = data.new_balance;
          this.userBalance = data.new_balance;

          this.spinDialog = false;
          this.resultsDialog = true;
        }
      } catch (error) {
        console.error("Error spinning:", error);
        if (error.response && error.response.data) {
          console.error(error.response.data.message || "Spin failed");
        }
      } finally {
        this.spinning = false;
      }
    },
    closeResultsDialog() {
      this.resultsDialog = false;
      this.spinResults = [];
      this.totalWinnings = 0;
      this.newBalance = 0;
    },
  },
};
</script>

<style scoped>
.wheel-card {
  transition: all 0.3s ease;
}

.wheel-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

.selected-wheel {
  border: 2px solid #e60040;
}

.gradient-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.rewards-preview {
  min-height: 60px;
}
</style>
